import { SITE_URL } from '@/shared/site';

interface Alternate {
  /** BCP 47 tag for hreflang. */
  lang: string;
  path: string;
}

interface PageHeadProps {
  title: string;
  description: string;
  /** Site path of this page; the canonical is SITE_URL + path. */
  path: string;
  /** The same page in every language, English first (it is also x-default). */
  alternates?: Alternate[];
  siteName: string;
  ogLocale: string;
  /** Absolute URL of a 1200×630 share card. */
  image: string;
  imageAlt: string;
  themeColor: string;
}

/**
 * Head tags for the Travo Labs root and MemberSheep pages. Travo's pages use SEO.tsx,
 * which is built around Travo's copy and five locales.
 */
export const PageHead = ({ title, description, path, alternates = [], siteName, ogLocale, image, imageAlt, themeColor }: PageHeadProps) => {
  const url = SITE_URL + path;

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {alternates.map((alt) => (
        <link key={alt.lang} rel="alternate" hrefLang={alt.lang} href={SITE_URL + alt.path} />
      ))}
      {alternates.length > 0 && <link rel="alternate" hrefLang="x-default" href={SITE_URL + alternates[0].path} />}

      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={ogLocale} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={imageAlt} />

      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
      <meta name="theme-color" content={themeColor} />
    </>
  );
};
